import { getViewportHeight } from './services/viewport';
import { initSearchPageSlot } from './modules/monetization/adsense-seacrh-ads';
import {
  DropdownController,
  DropdownModel,
  DropdownView,
  MODES,
} from './components/custom-dropdown';

const SEARCH_PARAMS = {
  CATEGORY_IDS: `category_ids`,
  SORT: `sort`,
  START_DATE: `start_date`,
  QUERY: `query`,
};

let nextPageUrl = null;
let hasMore = true;
let isLoading = false;

const makeBodyFixed = () => document.body.classList.add(`body-fixed`);
const removeBodyFixed = () => document.body.classList.remove(`body-fixed`);

const controllers = new Map();

const getFromUrlParamsValue = (key) => {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(key);

  if (key === SEARCH_PARAMS.CATEGORY_IDS) {
    if (value) {
      return value.split(`,`);
    }

    return [`ALL`];
  }

  if (value) {
    return [value];
  }

  if (!value) {
    if (key === SEARCH_PARAMS.SORT) {
      return [`relevance`];
    }

    if (key === SEARCH_PARAMS.START_DATE) {
      return [`ALL`];
    }
  }

  return [``];
};

const $categoriesContainer = document.getElementById(`category_ids`);
const categoryOptions = Object.entries(JSON.parse($categoriesContainer.dataset.values));
const modelCategories = new DropdownModel(
  categoryOptions,
  `ALL`,
  getFromUrlParamsValue(SEARCH_PARAMS.CATEGORY_IDS),
  MODES.MULTI,
  `ALL`,
);
const viewCategories = new DropdownView(
  $categoriesContainer,
  categoryOptions,
  SEARCH_PARAMS.CATEGORY_IDS,
  `Category`,
  MODES.MULTI,
);
const controllerCategories = new DropdownController(modelCategories, viewCategories);
controllers.set(SEARCH_PARAMS.CATEGORY_IDS, controllerCategories);

const $sortContainer = document.getElementById(`sort`);
const sortOptions = Object.entries(JSON.parse($sortContainer.dataset.values));
const modelSorting = new DropdownModel(
  sortOptions,
  `relevance`,
  getFromUrlParamsValue(SEARCH_PARAMS.SORT),
  MODES.SINGLE,
  ``,
);
const viewSorting = new DropdownView(
  $sortContainer,
  sortOptions,
  SEARCH_PARAMS.SORT,
  `Sort by`,
  MODES.SINGLE,
);
const controllerSorting = new DropdownController(modelSorting, viewSorting);

controllers.set(SEARCH_PARAMS.SORT, controllerSorting);

const $startDateContainer = document.getElementById(`start_date`);
const startDateOptions = Object.entries(JSON.parse($startDateContainer.dataset.values));
const modelStartDate = new DropdownModel(
  startDateOptions,
  `ALL`,
  getFromUrlParamsValue(SEARCH_PARAMS.START_DATE),
  MODES.SINGLE,
  ``,
);
const viewStartDate = new DropdownView(
  $startDateContainer,
  startDateOptions,
  SEARCH_PARAMS.START_DATE,
  `Date range`,
  MODES.SINGLE,
);
const controllerViewStartDate = new DropdownController(modelStartDate, viewStartDate);

controllers.set(SEARCH_PARAMS.START_DATE, controllerViewStartDate);

const $searchInputElement = document.getElementById(`search-input`);
const $filtersButton = document.getElementById(`search-filters`);
const $searchButton = document.getElementById(`search-button`);
const $filtersDialog = document.getElementById(`filters-dialog`);
const $clearFilters = document.getElementById(`clear-filters`);
const $dialogClearButton = document.getElementById(`dialog-clear-button`);
const $filtersForm = document.getElementById(`filters-form`);
const $filtersDialogClose = document.getElementById(`filters-dialog-close`);
const $searchForm = document.getElementById(`search-form`);

$filtersDialogClose.addEventListener(`click`, () => {
  $filtersDialog.close();
  removeBodyFixed();
});

$filtersButton.addEventListener(`click`, () => {
  $filtersDialog.showModal();
  makeBodyFixed();
});

const toggleView = ({ container, data, loader, showLoader = false }) => {
  container.innerHTML = data;
  showLoader ? loader.classList.remove(`hidden`) : loader.classList.add(`hidden`);
};

const getFormParams = (form) => {
  const formData = new FormData(form);
  const params = new URLSearchParams();

  for (const [key, value] of formData.entries()) {
    if (value.trim() !== `ALL` && value.trim() !== ``) {
      params.append(key, value);
    }
  }

  if ($searchInputElement.value.trim()) {
    params.append(SEARCH_PARAMS.QUERY, $searchInputElement.value.trim());
  }

  return params;
};

const renderContent = (data, append = false) => {
  const $container = document.getElementById(`articles-list`);
  const $noResultsContainer = document.getElementById(`no-results-container`);
  const $loaderContainer = document.getElementById(`js-search-page-skeleton`);
  const { maxTotalHits, totalHits, response, loadMoreUrl, hasMoreItems } = data;

  if (!append) {
    const $totalItemsContainer = document.getElementById(`results`);
    $totalItemsContainer.innerText =
      maxTotalHits === totalHits && totalHits !== 0 ? `${totalHits}+` : totalHits;
  }

  if (!data.response) {
    $noResultsContainer?.classList.remove(`hidden`);
    toggleView({ container: $container, data: null, loader: $loaderContainer, showLoader: false });
    return;
  }
  $noResultsContainer?.classList.add(`hidden`);

  if (!append) {
    toggleView({
      container: $container,
      data: response,
      loader: $loaderContainer,
      showLoader: false,
    });
  } else {
    $container.insertAdjacentHTML(`beforeend`, response);
  }
  nextPageUrl = loadMoreUrl;
  hasMore = hasMoreItems;
};

const fetchData = async (url) => {
  isLoading = true;
  try {
    const res = await fetch(url);

    if (!res.ok) {
      throw Error(`Failed to fetch data for search`);
    }

    const data = await res.json();
    return data;
  } catch (e) {
    console.error(`Error getting articles:`, e);
    return {
      response: null,
      hasMore: false,
      totalHits: 0,
      maxTotalHits: 0,
    };
  } finally {
    isLoading = false;
  }
};

const updateUrl = (params) => {
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.pushState({}, null, newUrl);
};

const observer = new IntersectionObserver(
  async (entries) => {
    const entry = entries[0];

    if (entry.isIntersecting && hasMore && !isLoading) {
      const data = await fetchData(nextPageUrl);
      renderContent(data, true);
    }
  },
  {
    root: null,
    rootMargin: `${-getViewportHeight()}px 0px ${getViewportHeight()}px 0px`,
    threshold: 0,
  },
);

window.addEventListener(`popstate`, async () => {
  const $loaderContainer = document.getElementById(`js-search-page-skeleton`);

  toggleView({
    container: document.getElementById(`articles-list`),
    data: null,
    loader: $loaderContainer,
    showLoader: true,
  });
  const fetchUrl = `${window.location.origin}/ajax/search/`;

  [...controllers.entries()].forEach(([key, controller]) => {
    controller.updateSelected(getFromUrlParamsValue(key));
  });
  $searchInputElement.value = getFromUrlParamsValue(`query`)[0];

  const params = getFormParams($filtersForm);
  const url = `${fetchUrl}?${params.toString()}`;
  const data = await fetchData(url);
  renderContent(data);
});

document.addEventListener(`DOMContentLoaded`, async () => {
  const $form = document.getElementById(`filters-form`);
  const fetchUrl = `${window.location.origin}/ajax/search/`;

  const params = getFormParams($form);
  const initialUrl = `${fetchUrl}?${params.toString()}`;
  const data = await fetchData(initialUrl);
  renderContent(data);

  const $infinitecontainer = document.getElementById(`infinite-container`);
  observer.observe($infinitecontainer);

  initSearchPageSlot(document.getElementById(`afscontainer`), params.get(`query`));

  $form.addEventListener(`submit`, (e) => e.preventDefault());
});

const $applyButton = document.getElementById(`apply-button`);

const applyFiltersAndRender = async () => {
  const newParams = getFormParams($filtersForm);
  const loaderContainer = document.getElementById(`js-search-page-skeleton`);
  const fetchUrl = `${window.location.origin}/ajax/search/`;
  $filtersDialog.close();
  removeBodyFixed();

  toggleView({
    container: document.getElementById(`articles-list`),
    data: null,
    loader: loaderContainer,
    showLoader: true,
  });
  updateUrl(newParams);

  const url = `${fetchUrl}?${newParams.toString()}`;
  const response = await fetchData(url);
  renderContent(response);
  initSearchPageSlot(document.getElementById(`afscontainer`), newParams.get(`query`));
};

[$applyButton, $searchButton]
  .filter(Boolean)
  .forEach((button) => button.addEventListener(`click`, applyFiltersAndRender));

$searchForm.addEventListener(`submit`, (e) => {
  e.preventDefault();
  applyFiltersAndRender();
});

const clearFilters = (withQuery = true) => {
  if (withQuery) {
    $searchInputElement.value = ``;
  }
  [...controllers.values()].forEach((controller) => controller.clear());
};

$clearFilters?.addEventListener(`click`, () => {
  clearFilters();
  applyFiltersAndRender();
});

$dialogClearButton.addEventListener(`click`, () => {
  clearFilters(false);
  applyFiltersAndRender();
});
